import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ArrowRight, Building2, Sparkles, ShieldCheck, ShieldAlert, Send } from "lucide-react";

const ROLE_COPY = {
  candidate: { label: "Candidate", blurb: "Apply to roles, track your pipeline, get paid notifications when status changes." },
  recruiter: { label: "Recruiter", blurb: "Post roles, manage applicants, and use the full HR portal — no separate Owner approval required." },
  moderator: { label: "Moderator", blurb: "Help the platform owner moderate content, accounts, and appeals." },
};

const TIER_COPY = {
  starter:    { label: "Starter",    note: "10 jobs · 1 free Featured boost / month · 10 team members" },
  growth:     { label: "Growth",     note: "Unlimited jobs · 3 Featured + 1 Premium / month · custom careers page" },
  enterprise: { label: "Enterprise", note: "Unlimited everything · white-label careers page · brand logo + colours · SLA support" },
};

export default function InviteShare() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState({ email: "", name: "", password: "", job_title: "", phone: "", company_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [signupError, setSignupError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invite/${encodeURIComponent(code)}`);
        setInvite(data);
      } catch (e) {
        setLoadError(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setSignupError(""); setSubmitting(true);
    try {
      // If invite locks a company (`invite.company` populated server-side), we
      // don't need to ask the recruiter for one — they join that company.
      // If it doesn't, the user is founding a NEW company and must supply a
      // name (validated below + server-side).
      const isRecruiterInvite = invite?.role_on_use === "recruiter";
      const needsCompany = isRecruiterInvite && !invite?.company;
      if (needsCompany && !form.company_name.trim()) {
        setSignupError("Please enter the company name for your team.");
        setSubmitting(false);
        return;
      }
      const payload = {
        email: form.email.toLowerCase().trim(),
        password: form.password,
        name: form.name.trim(),
        role: "candidate", // invite role_on_use takes over server-side
        invite_code: code.toUpperCase(),
        job_title: isRecruiterInvite ? (form.job_title || "Team member") : undefined,
        company_name: needsCompany ? form.company_name.trim() : undefined,
        phone: form.phone || undefined,
      };
      await api.post("/auth/register", payload);
      // Land them in the right place
      if (invite?.role_on_use === "recruiter") navigate("/recruiter");
      else if (invite?.role_on_use === "moderator") navigate("/admin");
      else navigate("/jobs");
    } catch (e) {
      setSignupError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  if (loadError) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto text-[var(--brand-primary)]" />
        <h1 className="font-display font-black text-3xl mt-5">Invite not found.</h1>
        <p className="text-[#5C5C5C] mt-3" data-testid="invite-error">{loadError}</p>
        <Link to="/register" className="btn-ink mt-8 inline-flex">Sign up without an invite</Link>
      </div>
    );
  }
  if (!invite) return <div className="p-16 text-center text-[#5C5C5C]">Loading invite…</div>;

  const role = ROLE_COPY[invite.role_on_use] || ROLE_COPY.candidate;
  const tier = invite.grant_tier ? TIER_COPY[invite.grant_tier] : null;
  const brandColor = invite.company?.brand_primary_color || "#D94833";
  const displayName = invite.company?.careers_display_name || invite.company?.name;

  if (!invite.valid) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto text-[#5C5C5C]" />
        <h1 className="font-display font-black text-3xl mt-5">This invite is no longer valid.</h1>
        <p className="text-[#5C5C5C] mt-3" data-testid="invite-invalid">
          {invite.revoked ? "It was revoked by the owner." : "All available seats on this invite are used."}
          {" "}You can still sign up — just without the perks attached to this code.
        </p>
        <Link to="/register" data-testid="invite-fallback-signup" className="btn-terra mt-8 inline-flex">Sign up</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
      <div className="border border-[#0F0F0F] p-8 lg:p-12 bg-[#FDFCF8]" style={{ borderColor: brandColor }}>
        <div className="flex items-center gap-3">
          {invite.company?.brand_logo_url ? (
            <img src={invite.company.brand_logo_url} alt="" className="w-12 h-12 object-contain" data-testid="invite-logo" />
          ) : (
            <Building2 className="w-10 h-10" style={{ color: brandColor }} />
          )}
          <div className="label-eyebrow">You've been invited</div>
        </div>
        <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tight mt-4">
          {displayName ? `Join ${displayName}.` : "You're invited to Ocean Ash."}
        </h1>
        <p className="text-[#5C5C5C] mt-3 text-lg">
          {role.blurb}
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          <div className="border border-[#E2E0D8] p-4">
            <div className="label-eyebrow flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Role</div>
            <div className="font-display font-bold text-xl mt-1" data-testid="invite-role">{role.label}</div>
          </div>
          {tier && (
            <div className="border p-4" style={{ borderColor: brandColor }}>
              <div className="label-eyebrow flex items-center gap-1.5" style={{ color: brandColor }}><Sparkles className="w-3 h-3" /> Plan grant — FREE</div>
              <div className="font-display font-bold text-xl mt-1" data-testid="invite-grant-tier">{tier.label}</div>
              <div className="text-xs text-[#5C5C5C] mt-1">{tier.note}</div>
            </div>
          )}
        </div>

        <form onSubmit={submit} className="mt-10 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow block mb-1.5">Your name</label>
            <input data-testid="invite-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Work email</label>
            <input data-testid="invite-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow block mb-1.5">Password (8+ chars)</label>
            <input data-testid="invite-password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" />
          </div>
          {invite.role_on_use === "recruiter" && (
            <div className="sm:col-span-2">
              <label className="label-eyebrow block mb-1.5">Your job title</label>
              <input data-testid="invite-job-title" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" placeholder="e.g. Talent Lead" />
            </div>
          )}
          {invite.role_on_use === "recruiter" && !invite.company && (
            <div className="sm:col-span-2">
              <label className="label-eyebrow block mb-1.5">Company name *</label>
              <input data-testid="invite-company-name" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="w-full border border-[#0F0F0F] bg-transparent px-3 py-2" placeholder="Acme Marine Ltd, Foundry Press, etc." />
              <p className="text-xs text-[#5C5C5C] mt-1">This invite isn&apos;t tied to a specific team yet, so we&apos;ll create a new company under your account.</p>
            </div>
          )}
          {signupError && <div data-testid="invite-signup-error" className="sm:col-span-2 text-sm text-[var(--brand-primary)]">{signupError}</div>}
          <button
            disabled={submitting}
            type="submit"
            data-testid="invite-accept"
            className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-5 py-3 font-display font-bold text-base text-white disabled:opacity-50"
            style={{ background: brandColor }}
          >
            <Send className="w-4 h-4" /> {submitting ? "Creating your account…" : `Accept invite & continue`} <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <p className="text-xs text-[#5C5C5C] mt-6">
          Code: <code className="font-mono font-bold tracking-widest">{invite.code}</code>
          {invite.uses_remaining > 0 && <span> · {invite.uses_remaining} seats remaining</span>}
        </p>
      </div>
    </div>
  );
}
